import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Table2, LineChart as LineChartIcon } from 'lucide-react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltipHost, ReferenceArea, ResponsiveContainer,
} from 'recharts';
import IconButton from '../../../components/ui/IconButton';
import Tooltip from '../../../components/ui/Tooltip';
import DashboardCard from './DashboardCard';
import ChartTooltip from './ChartTooltip';
import DayDrillStrip from './DayDrillStrip';
import { nonWorkingRuns, trendTableRows } from './chartHelpers';
import {
  Legend, LegendItem, NumTable, LineKey,
} from '../styles';
import {
  SERIES, SERIES_ORDER, SHADE, GRID, CROSSHAIR, CARD_IDS,
} from '../constants';
import {
  formatAxisDay, formatDayTitle, formatIsoDay, formatNumber,
} from '../format';

const SERIES_LABEL = {
  late: 'attendanceDashboard.series.late',
  early: 'attendanceDashboard.series.early',
  absent: 'attendanceDashboard.series.absent',
};
const DAY_KIND_LABEL = {
  weekend: 'attendanceDashboard.trend.weekend',
  holiday: 'attendanceDashboard.trend.holiday',
};

function TrendTooltip({
  active, payload, label, t, lang,
}) {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0]?.payload;
  if (!point || point.late === null || point.late === undefined) return null;
  return (
    <ChartTooltip
      title={formatDayTitle(label, lang)}
      subtitle={DAY_KIND_LABEL[point.dayKind] ? t(DAY_KIND_LABEL[point.dayKind]) : null}
      rows={SERIES_ORDER.map((kind) => ({
        key: kind,
        color: SERIES[kind],
        kind: 'line',
        // Bugün henüz bitmediği için devamsız değeri yok (null) — 0 yazılmaz.
        value: point[kind] === null || point[kind] === undefined ? '—' : formatNumber(point[kind], lang),
        label: t(SERIES_LABEL[kind]),
      }))}
      footnote={t('attendanceDashboard.trend.clickHint')}
    />
  );
}

// Günlük Eğilim: üç seri (geç, erken, devamsız) kişi sayısı; hafta sonu/tatil gölgeli; güne tıklayınca o günün
// listeleri. Amber düşük kontrastlı → lejant her zaman görünür + tablo görünümü.
export default function DailyTrendCard({ slice, query, effectiveTo }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [showTable, setShowTable] = useState(false);
  const [drillDate, setDrillDate] = useState(null);

  const days = useMemo(() => slice.data?.days || [], [slice.data]);
  const runs = useMemo(() => nonWorkingRuns(days), [days]);
  const rows = useMemo(() => trendTableRows(days), [days]);
  const drillDay = drillDate ? days.find((d) => d.date === drillDate) : null;

  const handleClick = useCallback((state) => {
    if (!query) return;   // panel listeye geçişe kapalı (özet yok / gelecek dönem)
    const index = Number(state?.activeTooltipIndex);
    const date = state?.activeLabel ?? (Number.isInteger(index) ? days[index]?.date : null);
    if (!date) return;
    if (effectiveTo && date > effectiveTo) return;   // gelecek gün — liste yok
    const point = days.find((d) => d.date === date);
    if (!point || point.late === null || point.late === undefined) return;
    setDrillDate(date);
  }, [days, effectiveTo, query]);

  const toggle = (
    <span data-export-exclude>
      <Tooltip text={showTable ? t('attendanceDashboard.table.hide') : t('attendanceDashboard.table.show')}>
        <IconButton
          type="button"
          size="sm"
          data-action="toggle-table"
          aria-label={showTable ? t('attendanceDashboard.table.hide') : t('attendanceDashboard.table.show')}
          onClick={() => setShowTable((v) => !v)}
        >
          {showTable ? <LineChartIcon size={14} /> : <Table2 size={14} />}
        </IconButton>
      </Tooltip>
    </span>
  );

  return (
    <DashboardCard
      cardKey="gunluk-egilim"
      title={t('attendanceDashboard.trend.title')}
      meta={t('attendanceDashboard.trend.meta')}
      span={8}
      spanMd={6}
      slice={slice}
      empty={days.length === 0}
      domId={CARD_IDS.trend}
      headerExtra={toggle}
    >
      <Legend style={{ marginTop: 0, marginBottom: 8 }}>
        {SERIES_ORDER.map((kind) => (
          <span key={kind} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <LineKey $color={SERIES[kind]} />
            {t(SERIES_LABEL[kind])}
          </span>
        ))}
        <LegendItem $color={SHADE}>{t('attendanceDashboard.trend.nonWorking')}</LegendItem>
      </Legend>
      {showTable ? (
        <NumTable data-view="table">
          <thead>
            <tr>
              <th>{t('attendanceDashboard.trend.date')}</th>
              {SERIES_ORDER.map((kind) => <th key={kind} className="n">{t(SERIES_LABEL[kind])}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.date} data-row={row.date}>
                <td>{formatIsoDay(row.date, lang)}</td>
                {SERIES_ORDER.map((kind) => (
                  <td key={kind} className="n">{row[kind] === null || row[kind] === undefined ? '—' : formatNumber(row[kind], lang)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </NumTable>
      ) : (
        <div data-view="chart">
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={days} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} onClick={handleClick}>
              <CartesianGrid vertical={false} stroke={GRID} strokeDasharray="0" />
              {runs.map((run) => (
                <ReferenceArea key={`${run.x1}-${run.x2}`} x1={run.x1} x2={run.x2} fill={SHADE} fillOpacity={1} strokeOpacity={0} />
              ))}
              <XAxis
                dataKey="date"
                tickFormatter={(value) => formatAxisDay(value, lang)}
                tickLine={false}
                axisLine={{ stroke: GRID }}
                tick={{ fontSize: 10, fill: '#6B7280' }}
                interval="preserveStartEnd"
                minTickGap={18}
              />
              <YAxis
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 10, fill: '#6B7280' }}
                width={32}
              />
              <ChartTooltipHost
                cursor={{ stroke: CROSSHAIR, strokeWidth: 1 }}
                content={<TrendTooltip t={t} lang={lang} />}
              />
              {SERIES_ORDER.map((kind) => (
                <Line
                  key={kind}
                  type="linear"
                  dataKey={kind}
                  name={t(SERIES_LABEL[kind])}
                  stroke={SERIES[kind]}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, stroke: '#fff', strokeWidth: 2 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
      {drillDay ? <DayDrillStrip day={drillDay} query={query} onClose={() => setDrillDate(null)} /> : null}
    </DashboardCard>
  );
}
